import { differenceInDays } from 'date-fns';

import normalizeJsonApi from '../../shared/utilities/jsonApiNormalizer';

import { ActionType } from '../../design-system/types';
import { datadogLogger } from '../../shared/datadog/logger';
import { getProcessFrom } from '../../domain';
import { formatDateTime, formatDateToUTC } from '../../shared/utilities/date';
import { DisplayProject, DossierOverviewsJSONAPIResponse } from '../dossier-overview/types';
import { InTripReviewFromAPI } from '../in-trip-review/types';
import { inTripReviewSerializer } from '../in-trip-review/serializers';
import { PreTripReviewFromAPI } from '../pre-trip-review/types';
import { preTripReviewSerializer } from '../pre-trip-review/serializers';
import { IncludedTag } from '../tags/api/api';

import { parseTravelRhythmExperiment } from './AB_travelRhythmSerializer';
import {
    AvailableTasks,
    AvailableTasksFromAPI,
    ContractDeferAgreementFromAPI,
    ContractVouchersFromAPI,
    CustomerCareCrisisTicket,
    CustomerCareCrisisTicketFromAPI,
    DeferAgreement,
    DossierId,
    DossierJSONAPIResponse,
    DossierOverview,
    DossierTag,
    IncludedParticipants,
    Memo,
    MemoFromAPI,
    NormalizedDossierEntry,
    Participants,
    Project,
    RelationshipValue,
    SerializedDossier,
    SerializedDossiers,
    Task,
    TaskDefinition,
    TaskNote,
    TravellerInformation,
    TravellerInformationFromAPI,
    TripProject,
    TripProjectField,
    TripProjectFieldKey,
    TripProjectJSONAPIResponse,
    Voucher,
} from './types';

export function projectSerializer({
    arrivalDate,
    approximateArrivalDate,
    approximateDurationInDays,
    departureDate,
    ...restOfProject
}: Project): DisplayProject {
    const arrivalDateInUTC = formatDateToUTC(arrivalDate);
    const departureDateInUTC = formatDateToUTC(departureDate);
    const approximateArrivalDateInUTC = formatDateToUTC(approximateArrivalDate);

    const duration =
        departureDateInUTC && arrivalDateInUTC
            ? differenceInDays(departureDateInUTC, arrivalDateInUTC) + 1
            : approximateDurationInDays;

    const expectedArrivalDate = arrivalDateInUTC ? arrivalDateInUTC : approximateArrivalDateInUTC;

    return {
        ...restOfProject,
        approximateDurationInDays,
        approximateArrivalDate: approximateArrivalDateInUTC,
        arrivalDate: arrivalDateInUTC,
        duration,
        expectedArrivalDate,
        departureDate: departureDateInUTC,
    };
}

type NormalizedTaskFromAPI = {
    dueDate: string;
    closeDate: string | null | undefined;
    definition: TaskDefinition;
    actionType: string;
    note: TaskNote;
};

const taskSerializer = (task: NormalizedTaskFromAPI): Task => ({
    dueDate: formatDateToUTC(task.dueDate) as Date,
    closeDate: formatDateToUTC(task.closeDate),
    definition: task.definition,
    actionType: task.actionType as ActionType,
    note: task.note,
});

function dossierSerializer(dossier: NormalizedDossierEntry, tags: DossierTag[]): DossierOverview {
    return {
        id: dossier.id,
        ...dossier.attributes,
        ...dossier.meta,
        agentId: dossier.attributes.agentId.toString(),
        archiveDate: formatDateTime(dossier.attributes.archiveDate),
        discussion: {
            ...dossier.attributes.discussion,
            lastAgentToTravellerMessageDate: formatDateTime(
                dossier.attributes.discussion.lastAgentToTravellerMessageDate,
            ),
            lastTravellerToAgentMessageDate: formatDateTime(
                dossier.attributes.discussion.lastTravellerToAgentMessageDate,
            ),
            lastMessageReadDate: formatDateTime(dossier.attributes.discussion.lastMessageReadDate),
        },
        process: getProcessFrom(dossier.attributes.step),
        project: projectSerializer(dossier.attributes.project),
        task: taskSerializer(dossier.attributes.task),
        testimonialReceptionDate: formatDateToUTC(dossier.attributes.testimonialReceptionDate),
        requestDate: formatDateTime(dossier.attributes.requestDate) as Date,
        tags,
    };
}

const memoSerializer = (memo: MemoFromAPI): Memo => memo.attributes.memo;

const participantsSerializer = (participants: IncludedParticipants): Participants =>
    participants.attributes;

type AvailableTasksEntry = { bySteps: AvailableTasks; suggested: TaskDefinition };

const availableTasksSerializer = (availableTasks: AvailableTasksFromAPI): AvailableTasksEntry => ({
    ...availableTasks.attributes,
    suggested: availableTasks.attributes.suggested.task_definition,
});

const customerCareCrisisTicketSerializer = (
    customerCareCrisisTicket: CustomerCareCrisisTicketFromAPI,
): CustomerCareCrisisTicket => customerCareCrisisTicket.attributes;

const contractVouchersSerializer = (contractVouchers: ContractVouchersFromAPI): Voucher[] =>
    contractVouchers.attributes.vouchers;

const contractDeferAgreementSerializer = (
    contractDeferAgreement: ContractDeferAgreementFromAPI,
): DeferAgreement => contractDeferAgreement.attributes.contractDeferAgreement;

const travellerInformationSerializer = (
    requesterId: number,
    travellerInformation: TravellerInformationFromAPI,
): TravellerInformation => ({
    ...travellerInformation.attributes,
    travellerId: requesterId,
    trips: travellerInformation.attributes.trips.map((trip) => ({
        ...trip,
        id: trip.id.toString(),
    })),
});

const dossierTagsMapper = (
    tags: Record<string, IncludedTag> | undefined,
    dossierTagsRelationships: RelationshipValue[] | RelationshipValue,
): DossierTag[] => {
    if (!tags || !Array.isArray(dossierTagsRelationships)) {
        return [];
    }

    return dossierTagsRelationships.map((tag) => ({
        color: `#${tags[tag.id].attributes.color}`,
        name: tags[tag.id].attributes.name,
        id: tags[tag.id].id,
    }));
};

export function serializeDossierFromAPI(json: DossierJSONAPIResponse): SerializedDossier {
    const dossierId = json.data.id;
    const {
        customerCareCrisisTicket,
        dossierOverview,
        dossierMemo,
        dossierParticipants,
        contractVouchers,
        contractDeferAgreement,
        preTripReview,
        inTripReview,
        travellerInformation,
        tag,
    } = normalizeJsonApi<{
        customerCareCrisisTicket?: Record<string, CustomerCareCrisisTicketFromAPI>;
        dossierOverview: Record<string, NormalizedDossierEntry>;
        dossierMemo?: Record<string, MemoFromAPI>;
        dossierParticipants?: Record<string, IncludedParticipants>;
        contractVouchers?: Record<string, ContractVouchersFromAPI>;
        contractDeferAgreement?: Record<string, ContractDeferAgreementFromAPI>;
        preTripReview?: Record<string, PreTripReviewFromAPI>;
        inTripReview?: Record<string, InTripReviewFromAPI>;
        travellerInformation?: Record<string, TravellerInformationFromAPI>;
        tag?: Record<string, IncludedTag>;
    }>(json);
    // This avoid to camelize object's keys which are step names and used as translations keys
    const availableTasks = normalizeJsonApi<{
        'available-tasks': { [key in DossierId]: AvailableTasksFromAPI };
    }>(json, { camelizeKeys: false })['available-tasks'];
    const { requesterId } = dossierOverview[dossierId].attributes.project;

    const mappedTags = dossierTagsMapper(
        tag,
        dossierOverview?.[dossierId]?.relationships?.tags?.data ?? [],
    );

    return {
        id: dossierId,
        dossier: {
            [dossierId]: dossierSerializer(dossierOverview[dossierId], mappedTags),
        },
        ...(dossierMemo && {
            dossierMemo: {
                [dossierId]: memoSerializer(dossierMemo[dossierId]),
            },
        }),
        ...(dossierParticipants && {
            dossierParticipants: {
                [dossierId]: participantsSerializer(dossierParticipants[dossierId]),
            },
        }),
        ...(availableTasks && {
            dossierAvailableTasks: {
                [dossierId]: availableTasksSerializer(availableTasks[dossierId]),
            },
        }),
        ...(customerCareCrisisTicket && {
            dossierCustomerCareCrisisTicket: {
                [dossierId]: customerCareCrisisTicketSerializer(
                    customerCareCrisisTicket[dossierId],
                ),
            },
        }),
        ...(contractVouchers && {
            contractVouchers: {
                [dossierId]: contractVouchersSerializer(contractVouchers[dossierId]),
            },
        }),
        ...(contractDeferAgreement && {
            contractDeferAgreement: {
                [dossierId]: contractDeferAgreementSerializer(contractDeferAgreement[dossierId]),
            },
        }),
        ...(preTripReview && {
            preTripReview: {
                [dossierId]: preTripReviewSerializer(preTripReview[dossierId]),
            },
        }),
        ...(inTripReview && {
            inTripReview: {
                [dossierId]: inTripReviewSerializer(inTripReview[dossierId]),
            },
        }),
        ...(travellerInformation && {
            travellerInformation: {
                [dossierId]: travellerInformationSerializer(
                    requesterId,
                    travellerInformation[requesterId],
                ),
            },
        }),
    };
}

export function serializeDossierOverviewFromAPI(
    json: DossierOverviewsJSONAPIResponse,
): SerializedDossiers {
    const { dossierOverview: dossiers, tag } = normalizeJsonApi<{
        dossierOverview: Record<string, NormalizedDossierEntry>;
        tag: Record<string, IncludedTag> | undefined;
    }>(json);

    return {
        dossier: dossiers
            ? Object.fromEntries(
                  Object.entries(dossiers).map(
                      ([key, dossier]: [string, NormalizedDossierEntry]) => {
                          if (!tag) {
                              return [key, dossierSerializer(dossier, [])];
                          }
                          const mappedTags = dossierTagsMapper(
                              tag,
                              dossier.relationships.tags?.data ?? [],
                          );
                          return [key, dossierSerializer(dossier, mappedTags)];
                      },
                  ),
              )
            : {},
    };
}

/**
 * Serializes trip project JSON API response into a HashMap for easy field access.
 *
 * Transforms array response into an object where field keys map to field data:
 * - `data.hosting` -> TripProjectFieldMultipleChoice (array values)
 * - `data.projectStep` -> TripProjectFieldSingle (string value)
 *
 * Validates that value types match fieldType to ensure runtime type safety.
 * Invalid fields are logged and skipped rather than throwing to prevent UI crashes.
 *
 * @param json - JSON API response from /dossier/:id/trip-project endpoint
 * @returns HashMap of field key to field data, with only valid recognized fields included
 */
export function tripProjectSerializer(json: TripProjectJSONAPIResponse): TripProject {
    if (!json.data || !Array.isArray(json.data)) {
        return {};
    }

    const result: Partial<Record<TripProjectFieldKey, TripProjectField>> = {};

    for (const field of json.data) {
        const { key, keyTranslated, value, valueTranslated, fieldType } = field.attributes;

        // Validate value type matches fieldType for runtime type safety
        if (fieldType === 'multipleChoice') {
            if (!Array.isArray(value) || !Array.isArray(valueTranslated)) {
                datadogLogger.error(
                    `[TripProject]: Invalid multipleChoice field ${key}: expected arrays`,
                    {
                        value,
                        valueTranslated,
                    },
                );
                continue; // Skip invalid field
            }
        } else {
            if (typeof value !== 'string' || typeof valueTranslated !== 'string') {
                datadogLogger.error(
                    `[TripProject]: Invalid ${fieldType} field ${key}: expected strings`,
                    {
                        value,
                        valueTranslated,
                    },
                );
                continue; // Skip invalid field
            }
        }

        result[key] = {
            key,
            keyTranslated,
            value,
            valueTranslated,
            fieldType,
        } as TripProjectField;
    }

    const output: TripProject = result as TripProject;

    /* BEGIN - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */
    const experiment = parseTravelRhythmExperiment(json.meta);
    if (experiment) output.experiment = experiment;
    /* END - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */

    return output;
}
